import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import {
  User,
  Building,
  Phone,
  Mail,
  Globe,
  MapPin,
  Download,
  Lock,
  Sparkles,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Check,
  Briefcase
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Basic Info', icon: User },
  { id: 2, title: 'Contact', icon: Phone },
  { id: 3, title: 'Address', icon: MapPin },
];

const VCardQRGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const qrRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    address: '',
  });
  
  const [showPreview, setShowPreview] = useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const minSwipeDistance = 50;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndX(null);
    setTouchStart(e.targetTouches[0].clientY);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartX || !touchEndX) return;
    
    const distanceY = touchStart - touchEnd;
    const distanceX = touchStartX - touchEndX;
    
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    
    if (isHorizontalSwipe) {
      const isSwipeLeft = distanceX > minSwipeDistance;
      const isSwipeRight = distanceX < -minSwipeDistance;
      
      if (isSwipeLeft && !showPreview && currentStep < 3) {
        nextStep();
      } else if (isSwipeRight && !showPreview && currentStep > 1) {
        prevStep();
      }
    }
    // Removed swipe up/down for preview - only keep horizontal swipe for step navigation
  };

  const nextStep = () => {
    if (currentStep === 1 && !formData.firstName.trim()) {
      toast({ title: 'Name Required', description: 'Please enter first name', variant: 'destructive' });
      return;
    }
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowPreview(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const generateVCard = () => {
    return [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `N:${formData.lastName};${formData.firstName};;;`,
      `FN:${formData.firstName} ${formData.lastName}`,
      formData.title && `TITLE:${formData.title}`,
      formData.company && `ORG:${formData.company}`,
      formData.email && `EMAIL:${formData.email}`,
      formData.phone && `TEL:${formData.phone}`,
      formData.website && `URL:${formData.website}`,
      formData.address && `ADR:;;${formData.address};;;;`,
      'END:VCARD'
    ].filter(Boolean).join('\n');
  };

  const handleDownload = () => {
    if (!user) {
      setShowSignInPrompt(true);
      return;
    }
    
    const canvas = qrRef.current?.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `vcard-${formData.firstName || 'contact'}-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast({ title: 'Downloaded!', description: 'vCard QR code saved.' });
    }
  };

  const handleDownloadVCard = () => {
    if (!user) {
      setShowSignInPrompt(true);
      return;
    }
    
    const vcard = generateVCard();
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const link = document.createElement('a');
    link.download = `${formData.firstName || 'contact'}.vcf`;
    link.href = URL.createObjectURL(blob);
    link.click();
    toast({ title: 'Downloaded!', description: 'vCard file saved.' });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-1.5 block">First Name <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    placeholder="John"
                    className="pl-10 h-12"
                    autoFocus
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Last Name</Label>
                <Input
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Doe"
                  className="h-12"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm mb-1.5 block">Job Title</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Marketing Manager"
                  className="pl-10 h-12"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm mb-1.5 block">Company</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  placeholder="Acme Inc"
                  className="pl-10 h-12"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-1.5 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@example.com"
                  className="pl-10 h-12"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm mb-1.5 block">Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+91 98765 43210"
                  className="pl-10 h-12"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm mb-1.5 block">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://example.com"
                  className="pl-10 h-12"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-1.5 block">Address</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="123 Main St, City, Country"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                This address will be included in the vCard when someone scans your QR code
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const vcardData = generateVCard();

  return (
    <Layout>
      <SEO
        title="Free vCard QR Code Generator | Create Contact QR Codes"
        description="Generate vCard QR codes for instant contact sharing. Create scannable QR codes that save your contact info directly to any phone."
        keywords="vCard QR code generator, contact QR code, digital business card QR, vCard creator free"
      />

      <div 
        className="min-h-screen bg-gray-50"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-green-500" />
              <span className="font-bold text-lg text-gray-900">vCard QR</span>
            </div>
            <button onClick={() => setShowPreview(true)} className="text-green-600 font-medium text-sm">
              Preview
            </button>
          </div>
        </header>

        {/* Progress Steps */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-lg mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center gap-2 ${currentStep >= step.id ? 'text-green-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      currentStep > step.id ? 'bg-green-500 text-white' :
                      currentStep === step.id ? 'bg-green-100 text-green-600 border-2 border-green-500' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
                    </div>
                    <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-8 sm:w-16 h-0.5 mx-2 ${currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-lg mx-auto px-4 py-6 pb-32">
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {STEPS[currentStep - 1].title}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {currentStep === 1 && "Enter your basic information"}
              {currentStep === 2 && "Add your contact details"}
              {currentStep === 3 && "Add your address (optional)"}
            </p>
          </div>

          <Card className="shadow-sm border border-gray-100">
            <CardContent className="p-5">
              {renderStepContent()}
            </CardContent>
          </Card>
        </main>

        {/* Fixed Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="max-w-lg mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep} className="flex-1 h-12">
                  <ChevronLeft className="w-5 h-5 mr-1" /> Back
                </Button>
              ) : (
                <div className="flex-1" />
              )}
              
              <Button
                onClick={nextStep}
                className="flex-1 h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
              >
                {currentStep === 3 ? (
                  <>Generate QR</>
                ) : (
                  <>Next <ChevronRight className="w-5 h-5 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent 
          className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto p-0"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          <p className="text-xs text-center text-gray-400 mb-2">Swipe down to close</p>
          
          <DialogHeader className="px-4 pb-3 border-b">
            <DialogTitle className="text-lg font-semibold text-center">vCard QR Code</DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            <div ref={qrRef} className="flex justify-center mb-4">
              <div className="p-4 bg-white rounded-xl shadow-lg">
                <QRCodeCanvas
                  value={vcardData}
                  size={200}
                  level="M"
                  includeMargin={true}
                />
              </div>
            </div>

            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm">
                <CreditCard className="w-4 h-4" />
                Scan to save contact
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={handleDownload}
                className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
              >
                {user ? (
                  <><Download className="w-5 h-5 mr-2" /> Download QR Code</>
                ) : (
                  <><Lock className="w-5 h-5 mr-2" /> Sign In to Download</>
                )}
              </Button>
              
              <Button variant="outline" onClick={handleDownloadVCard} className="w-full h-10">
                {user ? (
                  <><Download className="w-4 h-4 mr-2" /> Download .vcf File</>
                ) : (
                  <><Lock className="w-4 h-4 mr-2" /> Sign In for .vcf</>
                )}
              </Button>
            </div>

            {showSignInPrompt && !user && (
              <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
                <p className="text-green-800 font-medium mb-3">
                  Create a free account to download your vCard
                </p>
                <Button onClick={() => navigate('/card-builder')} className="bg-green-600 hover:bg-green-700">
                  Sign Up Free
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default VCardQRGenerator;
